import { useRef, useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import Close from '../../assets/Close.jsx';
import Pixel from '../../assets/Pixel.jsx';
import VideoControlsEl from '../../components/common/VideoControls.jsx';
import { videoControls } from '../../utilities/videoControls.js';
import toSentenceCase from '../../utilities/sentenceCase.js';
import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import { useParams } from 'react-router-dom';

export default function QuickView({ isOpen, onClose, isAnimating, project }) {
    const { lang } = useParams();

    const quickViewRef = useRef();
    const videoRef = useRef();
    const videoWrapRef = useRef();

    const [isVisible, setIsVisible] = useState(false);
    const [isActive, setIsActive] = useState(false);

    // Determine the correct video source (HD/SD) once on mount
    const videoSrc = useMemo(() => {
        if (!project) return null;
        const hd = project.fields.fullVideoHdLink;
        const sd = project.fields.fullVideoSdLink;
        const isHD = window.innerWidth > 1280;
        return isHD ? hd : sd;
    }, [project]);

    const imageSrc = useMemo(() => {
        return project ? `https:${project.fields.gridImage.fields.file.url}` : null;
    }, [project]);

    const summaryHtml = useMemo(() => {
        return project?.fields.summary
            ? documentToHtmlString(project.fields.summary)
            : null;
    }, [project]);

    const headline = useMemo(() => {
        if (!project) return '';
        return project.fields.headline || toSentenceCase(project.fields.title);
    }, [project]);

    const projectSlug = project?.fields.slug;

    // Mount effect to trigger animation
    useEffect(() => {
        if (isOpen) {
            setIsVisible(true);

            requestAnimationFrame(() => {
                // Let it render once first, then apply animation class
                requestAnimationFrame(() => {
                    setIsActive(true); // This triggers the slide-in animation
                });
            });

            document.body.classList.add("overlay");

            // Add keyboard/click outside listeners
            document.addEventListener("keydown", handleKeyDown);
            document.addEventListener("mousedown", handleClickOutside);

        } else {
            setIsActive(false);
            document.body.classList.remove("overlay");

            if (videoRef.current) {
                videoRef.current.pause();
            }

            setTimeout(() => {
                setIsVisible(false);
            }, 600); // Match your transition duration
        }

        return () => {
            document.body.classList.remove('overlay');
            document.removeEventListener("keydown", handleKeyDown);
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    // Attach custom video controls after render
    useEffect(() => {
        if (videoRef.current && videoWrapRef.current && videoSrc) {
            videoControls(videoWrapRef);
        }
    }, [videoSrc]);

    const handleKeyDown = (event) => {
        if (event.key === 'Escape') {
            onClose();
        }
    };

    const handleClickOutside = (event) => {
        if (quickViewRef.current && !quickViewRef.current.contains(event.target)) {
            onClose();
        }
    };

    if (!isVisible || !project) return null;

    return ReactDOM.createPortal(
        <div id="quickview" className={`project ${isActive ? 'active' : ''}`} ref={quickViewRef}>
            <div className="header">
                <div className="close" onClick={onClose}>
                    <Close /> {lang === "ko" ? "닫기" : "CLOSE"}
                </div>
                <div className="view">
                    <Pixel />
                    <a href={`/${lang}/projects/${projectSlug}`}>
                        {lang === "ko" ? "자세히 보기" : "SEE CASE STUDY"}
                    </a>
                </div>
            </div>

            <div className="body">
                <h2 className="headline preserve-line-breaks">{headline}</h2>

                <div className="media">
                    {videoSrc ? (
                        <div className="fullvideo" ref={videoWrapRef}>
                            <VideoControlsEl isAnimating={isAnimating} hdSrc={project.fields.fullVideoHdLink} sdSrc={project.fields.fullVideoSdLink} />
                        </div>
                    ) : (
                        <img src={imageSrc} className="photo" alt={project.fields.title} />
                    )}
                </div>

                {summaryHtml && (
                    <div className="summary">
                        <div className="title">{lang === "ko" ? "소개" : "ABOUT"}</div>
                        <div className="details" dangerouslySetInnerHTML={{ __html: summaryHtml }} />
                    </div>
                )}

                <div className={`link ${!summaryHtml ? 'no_summary' : ''}`}>
                    <div className="contents">
                        <Pixel color="#017863" />
                        <a href={`/${lang}/projects/${projectSlug}`}>
                            {lang === "ko" ? "자세히 보기" : "SEE CASE STUDY"}
                        </a>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}
